import { useEffect } from 'react';
import { Hub } from 'aws-amplify';
import { captureAttribution, primeAttributionToken } from '../services/adAttribution';
import { flushFunnel } from '../services/funnel';
import { getCachedIdToken } from '../../services/network';
import { isIOS, isNative } from '../utils/platform';

// After launch has settled — splash, StoreKit set-up and the first screen
// come first; the token call is a synchronous native call that can be slow.
const START_DELAY_MS = 3000;
// Amplify announces signIn as the session is stored; give it a moment.
const AFTER_SIGN_IN_DELAY_MS = 2000;

/**
 * Apple Ads attribution and the funnel queue (docs/v150-spec.md §3.8, §2.9).
 * Mount once at the app root (it runs signed out too). Shortly after launch
 * it mints and keeps an attribution token when nobody is signed in; when
 * someone is, it exchanges the token and flushes queued funnel events (those
 * from the signed-out onboarding screens, or left by a failed send) — and does
 * both again after a sign-in in the same session. Attribution is native iOS
 * only; the funnel flush runs everywhere. Everything it calls does its own
 * bookkeeping and never rejects.
 */
export function useAttributionCapture() {
  useEffect(() => {
    const ios = isNative() && isIOS();
    const timers = [];

    timers.push(
      setTimeout(() => {
        if (getCachedIdToken()) {
          flushFunnel();
          if (ios) captureAttribution();
        } else if (ios) {
          primeAttributionToken();
        }
      }, START_DELAY_MS)
    );

    let stopListening = () => {};
    try {
      stopListening = Hub.listen('auth', ({ payload }) => {
        if (payload?.event === 'signIn' || payload?.event === 'autoSignIn') {
          timers.push(
            setTimeout(() => {
              flushFunnel();
              if (ios) captureAttribution();
            }, AFTER_SIGN_IN_DELAY_MS)
          );
        }
      });
    } catch {
      /* Hub unavailable — the next launch catches up instead */
    }

    return () => {
      timers.forEach(clearTimeout);
      try {
        stopListening();
      } catch {
        /* already removed */
      }
    };
  }, []);
}

export default useAttributionCapture;
